import { unwrapConnection } from "@utils/utils";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportEhrSyncChoices,
  useConvertReportGoalsToProgrammingReviewGoalsMutation,
  useLearnerByIdQuery,
} from "@api/graphql/types-and-hooks";
import { SyncToEHRDialog } from "@components/ProgrammingReview/SyncToEHRDialog";
import {
  ProgrammingReviewHeader,
  ProgrammingReviewLeftPanel,
  ProgrammingReviewRightPanel,
  ProgrammingReviewWelcomeDialog,
} from "@pages/ClientProgramming/ProgrammingReview/components";
import { useAdminData } from "@providers/AdminDataProvider";
import { useProgrammingReviewData } from "@providers/ProgrammingReviewProvider";

export const ProgrammingReview: React.FC = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { isPassageHealthUser } = useAdminData();

  // UI State
  const [isSyncToEHRDialogOpen, setIsSyncToEHRDialogOpen] = useState(false);
  const [isWelcomeDialogOpen, setIsWelcomeDialogOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [conversionError, setConversionError] = useState<string | null>(null);

  // Provider data
  const {
    getProgramReviewGoalsQuery: { isFetching, refetch: refetchProgramReviewGoals },
    programReviewGoalList,
    latestInitialAssessmentReportQuery,
  } = useProgrammingReviewData();

  // Calculate totals for sync dialog
  const totalGoals = programReviewGoalList?.length ?? 0;
  const totalTargets = (programReviewGoalList ?? []).reduce(
    (total, goal) => total + (goal.passageTargets?.edges.length ?? 0),
    0,
  );

  // Filter goals based on search text

  const { data: reportsData, isLoading: isLoadingReports } = latestInitialAssessmentReportQuery;
  const latestInitialAssessmentReport = unwrapConnection(reportsData?.getReports)?.[0];
  const assessmentReportId = latestInitialAssessmentReport?.id ?? null;

  // Check if sync is completed - if so, make forms read-only (but keep layout the same)
  const isSyncCompleted = latestInitialAssessmentReport?.ehrSync === ApiReportEhrSyncChoices.Completed;

  // Learner details query
  const learnerDetailsQuery = useLearnerByIdQuery(
    { learnerId: clientId ?? "" },
    {
      queryKey: ["learner-by-id", clientId],
      enabled: !!clientId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const clientName = learnerDetailsQuery.data?.learnerById
    ? `${learnerDetailsQuery.data.learnerById.learnermetadata?.firstName || ""} ${learnerDetailsQuery.data.learnerById.learnermetadata?.lastName || ""}`.trim()
    : "";

  // Convert goals mutation
  const convertGoalsMutation = useConvertReportGoalsToProgrammingReviewGoalsMutation({
    onSuccess: (data) => {
      if (data.convertReportGoalsToProgrammingReviewGoals?.status) {
        refetchProgramReviewGoals();
        setIsWelcomeDialogOpen(false);
      } else {
        setConversionError(data.convertReportGoalsToProgrammingReviewGoals?.message || "Failed to convert goals");
      }
    },
    onError: (error) => {
      console.error("Error converting goals:", error);
      setConversionError("An error occurred while converting goals. Please try again.");
    },
  });

  // Determine if first visit (no goals exist yet)
  // Don't show welcome dialog if sync is already completed
  useEffect(() => {
    if (!isFetching && programReviewGoalList !== undefined) {
      const hasNoGoals = (programReviewGoalList ?? []).length === 0;
      setIsWelcomeDialogOpen(hasNoGoals && !isSyncCompleted);
      setIsInitializing(false);
    }
  }, [isFetching, programReviewGoalList, isSyncCompleted]);

  // Handlers
  const handleStartReview = () => {
    if (!clientId) {
      setConversionError("Missing learner ID");
      return;
    }

    if (!latestInitialAssessmentReport?.id) {
      setConversionError("No initial assessment report found for this learner");
      return;
    }

    setConversionError(null);
    convertGoalsMutation.mutate({
      learnerId: clientId,
      reportId: latestInitialAssessmentReport.id,
    });
  };

  const handleCancelWelcome = () => {
    setIsWelcomeDialogOpen(false);
    window.history.back();
  };

  // Loading state
  if (isInitializing) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Paragraph size="large" colorType="primary" displayType="loud">
          Loading...
        </Paragraph>
      </div>
    );
  }

  // Welcome dialog for first-time users
  if (isWelcomeDialogOpen) {
    return (
      <ProgrammingReviewWelcomeDialog
        isOpen={isWelcomeDialogOpen}
        clientName={clientName}
        isLoading={isLoadingReports}
        isPending={convertGoalsMutation.isPending}
        canStartReview={!isLoadingReports && !!latestInitialAssessmentReport?.id}
        hasReport={!!latestInitialAssessmentReport}
        error={conversionError}
        onStart={handleStartReview}
        onCancel={handleCancelWelcome}
      />
    );
  }

  // Already synced state - goals have been synced to EHR
  const hasNoGoals = (programReviewGoalList ?? []).length === 0;
  if (isSyncCompleted && hasNoGoals) {
    return (
      <div className="-mt-8 flex w-full flex-col items-start justify-between">
        <div className="d-flex flex-col sticky top-4 pt-3 w-full z-20">
          <div className="-mt-5 flex flex-col gap-y-2 bg-white -mx-12 p-2 border-b border-limestone-200">
            <ProgrammingReviewHeader
              clientName={clientName}
              isEHROrgUser={isPassageHealthUser}
              assessmentReportId={assessmentReportId}
              ehrSyncStatus={latestInitialAssessmentReport?.ehrSync}
              onSyncClick={() => setIsSyncToEHRDialogOpen(true)}
            />
          </div>
        </div>
        <div className="w-full flex-1 flex flex-col items-center justify-center gap-6 py-20">
          <Headline size="small">Programming Already Synced</Headline>
          <Paragraph colorType="secondary" className="text-center max-w-xl">
            The goals for <span className="ph-no-capture">{clientName || "this learner"}</span> have already been synced
            to the EHR. <br></br>
            You can view the programming in read-only mode.
          </Paragraph>
          <Button
            text="Go to Programming"
            appearance="primary"
            onClick={() => navigate(`/clients/${clientId}/programming`)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="-mt-8 flex w-full flex-col items-start justify-between">
      <div className="d-flex flex-col sticky top-4 pt-3 w-full z-20">
        <div className="-mt-5 flex flex-col gap-y-2 bg-white -mx-12 p-2 border-b border-limestone-200">
          <ProgrammingReviewHeader
            clientName={clientName}
            isEHROrgUser={isPassageHealthUser}
            assessmentReportId={assessmentReportId}
            ehrSyncStatus={latestInitialAssessmentReport?.ehrSync}
            onSyncClick={() => setIsSyncToEHRDialogOpen(true)}
          />
        </div>
      </div>
      <div className="w-[calc(100%+6rem)] h-[calc(100vh-8rem)] -mx-12">
        <div className="flex h-full w-full">
          <ProgrammingReviewLeftPanel isReadOnly={isSyncCompleted} />
          <ProgrammingReviewRightPanel isReadOnly={isSyncCompleted} />
        </div>
        {assessmentReportId && (
          <SyncToEHRDialog
            dialogOpen={isSyncToEHRDialogOpen}
            setDialogOpen={setIsSyncToEHRDialogOpen}
            disabled={false}
            goalsCount={totalGoals}
            targetsCount={totalTargets}
            assessmentReportId={assessmentReportId}
          />
        )}
      </div>
    </div>
  );
};
